import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Gauge,
  Clock,
  ArrowRight,
  BellRing,
  Activity,
} from "lucide-react";
import { fetchStats, fetchDispensaries, fetchDiscoveryStats, fetchInteractionStats } from "../lib/api";
import { RunScanButton } from "../components/RunScanButton";
import { ExportMenu } from "../components/ExportMenu";
import { DataCompletenessCard } from "../components/DataCompletenessCard";
import { PipelineGlanceCard } from "../components/PipelineGlanceCard";
import { UpcomingTastingsCard } from "../components/UpcomingTastingsCard";
import { TeamActivityCard } from "../components/TeamActivityCard";
import { AssignedTasksCard } from "../components/AssignedTasksCard";
import { PendingAssignmentRequestsCard } from "../components/PendingAssignmentRequestsCard";
import { HotDayCallout } from "../components/HotDayCallout";
import { SalesScorecardCard } from "../components/SalesScorecardCard";
import { Skeleton } from "../components/ui/skeleton";

function KPI({ label, value, icon: Icon, accent = "text-zinc-50", testid }) {
  return (
    <div
      data-testid={testid}
      className="panel p-5 flex flex-col gap-3 hover:border-zinc-700 transition-colors duration-150"
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.12em] font-medium text-zinc-500 font-mono">
          {label}
        </span>
        <Icon className="w-4 h-4 text-zinc-600" />
      </div>
      <div className={`text-3xl font-semibold tracking-tight ${accent}`}>
        {value}
      </div>
    </div>
  );
}

function RowItem({ d }) {
  // Count how many of the 6 primary contact fields are populated for a
  // mini per-row "completeness" indicator.
  const filled = [
    d.phone, d.email, d.website, d.hours, d.instagram_url, d.weedmaps_url,
  ].filter((v) => v && String(v).trim()).length;
  const totalFields = 6;
  return (
    <Link
      to={`/dispensaries/${d.id}`}
      data-testid={`recent-row-${d.id}`}
      className="grid grid-cols-12 gap-3 items-center px-3 py-2.5 rounded-md hover:bg-zinc-900/60 transition-colors duration-150 text-sm"
    >
      <div className="col-span-6">
        <div className="text-zinc-50 font-medium truncate">{d.name}</div>
        <div className="text-xs text-zinc-500 truncate">
          {d.city}{d.county ? ` · ${d.county}` : ""}
        </div>
      </div>
      <div className="col-span-4 flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              filled === totalFields
                ? "bg-emerald-500"
                : filled >= 4
                ? "bg-amber-500"
                : "bg-rose-500/70"
            }`}
            style={{ width: `${(filled / totalFields) * 100}%` }}
          />
        </div>
        <span className="text-[10px] font-mono text-zinc-500 w-8 text-right">
          {filled}/{totalFields}
        </span>
      </div>
      <div className="col-span-2 flex justify-end text-zinc-600 group-hover:text-zinc-300">
        <ArrowRight className="w-4 h-4" />
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [activity, setActivity] = useState(null);

  const load = async () => {
    const [s, r, c, a] = await Promise.all([
      fetchStats(),
      fetchDispensaries({ sort: "recently_updated", limit: 8 }),
      fetchDiscoveryStats(),
      fetchInteractionStats().catch(() => null),
    ]);
    setStats(s);
    setRecent(r);
    setCompleteness(c);
    setActivity(a);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="max-w-[1600px] mx-auto space-y-6 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Overview</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Overview of your dispensary research database.
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="dashboard-actions">
          <ExportMenu />
          <RunScanButton onComplete={load} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats ? (
          <>
            <KPI
              testid="kpi-total"
              label="Total Dispensaries"
              value={stats.total_dispensaries.toLocaleString()}
              icon={Building2}
            />
            <KPI
              testid="kpi-completeness"
              label="Completeness Score"
              value={`${completeness?.completeness_score ?? "—"}${completeness?.completeness_score != null ? "%" : ""}`}
              icon={Gauge}
              accent={
                (completeness?.completeness_score ?? 0) >= 80
                  ? "text-emerald-400"
                  : (completeness?.completeness_score ?? 0) >= 50
                  ? "text-amber-400"
                  : "text-rose-300"
              }
            />
            <KPI
              testid="kpi-activity-7d"
              label="Team Activity 7d"
              value={(activity?.last_7_days ?? 0).toLocaleString()}
              icon={Activity}
              accent={
                (activity?.last_7_days ?? 0) >= 10
                  ? "text-emerald-300"
                  : (activity?.last_7_days ?? 0) >= 1
                  ? "text-amber-300"
                  : "text-zinc-500"
              }
            />
            <KPI
              testid="kpi-license-alerts"
              label="License Alerts"
              value={(stats.license_alerts ?? 0).toLocaleString()}
              icon={BellRing}
              accent={stats.license_alerts ? "text-rose-400" : "text-zinc-300"}
            />
          </>
        ) : (
          [0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[112px] rounded-xl bg-zinc-900/50" />
          ))
        )}
      </div>

      <DataCompletenessCard />

      {/* Priority callouts — only render when there's something to flag */}
      <HotDayCallout />

      {/* Approvals — only renders if pending requests exist */}
      <PendingAssignmentRequestsCard />

      {/* Action zone — what's been delegated + what's coming up */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AssignedTasksCard limit={8} />
        <UpcomingTastingsCard limit={4} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <TeamActivityCard days={7} />
        </div>
        <div className="lg:col-span-2">
          <SalesScorecardCard />
        </div>
      </div>

      {/* Reference zone — recent records + funnel snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="panel p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium tracking-tight">Recently Updated</h3>
            <Link
              to="/dispensaries"
              data-testid="view-all-dispensaries"
              className="text-xs text-emerald-400 hover:text-emerald-300 font-mono"
            >
              View all →
            </Link>
          </div>
          <div className="space-y-0.5">
            <div className="grid grid-cols-12 gap-3 px-3 pb-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-mono border-b border-zinc-800">
              <div className="col-span-6">Dispensary</div>
              <div className="col-span-4">Contact Data</div>
              <div className="col-span-2"></div>
            </div>
            {recent ? (
              recent.length ? (
                recent.map((d) => <RowItem key={d.id} d={d} />)
              ) : (
                <div className="px-3 py-8 text-center text-sm text-zinc-500">
                  No dispensaries yet — click "Run Scan" to populate.
                </div>
              )
            ) : (
              [0, 1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={i}
                  className="h-10 my-1 rounded-md bg-zinc-900/50"
                />
              ))
            )}
          </div>
        </div>

        <PipelineGlanceCard />
      </div>
    </div>
  );
}
